import { useRef, useEffect, useImperativeHandle, forwardRef } from "react";

const CKEDITOR_LICENSE_KEY = "d2FMa1UzTTJ0bEJtRC9TWktQM1NOYnRMdjlBZmZ6aVdBdEtCQjBWamFrR1lad0d3V0RpcUdEYz0tTWpBeU5UQXpNekU9";
const CKEDITOR_SCRIPT_ID = "mable-home-ckeditor-script";
const CKEDITOR_CDN_URL = "https://cdn.ckeditor.com/ckeditor5/41.4.2/super-build/ckeditor.js";

interface RichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  className?: string;
}

export interface RichTextEditorHandle {
  insertText: (text: string) => void;
}

interface CKEditorInstance {
  getData: () => string;
  setData: (data: string) => void;
  destroy: () => Promise<void>;
  execute: (command: string, options?: Record<string, unknown>) => void;
  model: {
    document: {
      selection: {
        focus: unknown;
        getAttributes: () => Iterable<[string, unknown]>;
      };
      on: (event: string, callback: () => void) => void;
    };
    change: (callback: (writer: CKWriter) => void) => void;
  };
}

interface CKWriter {
  insertText: (text: string, attributes: Iterable<[string, unknown]>, position: unknown) => void;
}

declare global {
  interface Window {
    CKEDITOR?: {
      ClassicEditor: {
        create: (element: HTMLElement, config: Record<string, unknown>) => Promise<CKEditorInstance>;
      };
    };
    ClassicEditor?: {
      create: (element: HTMLElement, config: Record<string, unknown>) => Promise<CKEditorInstance>;
    };
  }
}

const EDITOR_CONFIG: Record<string, unknown> = {
  toolbar: {
    items: [
      "findAndReplace", "selectAll", "|",
      "heading", "|",
      "bold", "italic", "strikethrough", "underline", "code", "subscript", "superscript", "removeFormat", "|",
      "bulletedList", "numberedList", "todoList", "|",
      "outdent", "indent", "|",
      "undo", "redo",
      "-",
      "fontSize", "fontFamily", "fontColor", "fontBackgroundColor", "highlight", "|",
      "alignment", "|",
      "link", "uploadImage", "blockQuote", "insertTable", "codeBlock", "|",
      "specialCharacters", "horizontalLine", "pageBreak", "|",
      "sourceEditing",
    ],
    shouldNotGroupWhenFull: true,
  },
  list: {
    properties: { styles: true, startIndex: true, reversed: true },
  },
  heading: {
    options: [
      { model: "paragraph", title: "Paragraph", class: "ck-heading_paragraph" },
      { model: "heading1", view: "h1", title: "Heading 1", class: "ck-heading_heading1" },
      { model: "heading2", view: "h2", title: "Heading 2", class: "ck-heading_heading2" },
      { model: "heading3", view: "h3", title: "Heading 3", class: "ck-heading_heading3" },
      { model: "heading4", view: "h4", title: "Heading 4", class: "ck-heading_heading4" },
      { model: "heading5", view: "h5", title: "Heading 5", class: "ck-heading_heading5" },
      { model: "heading6", view: "h6", title: "Heading 6", class: "ck-heading_heading6" },
    ],
  },
  placeholder: "Notification",
  fontFamily: {
    options: [
      "Aptos, sans-serif",
      "Aptos Light, sans-serif",
      "Arial, Helvetica, sans-serif",
      "Courier New, Courier, monospace",
      "Georgia, serif",
      "Lucida Sans Unicode, Lucida Grande, sans-serif",
      "Tahoma, Geneva, sans-serif",
      "Times New Roman, Times, serif",
      "Trebuchet MS, Helvetica, sans-serif",
      "Verdana, Geneva, sans-serif",
      "Calibri",
      "Calibri Light",
    ],
    supportAllValues: true,
  },
  fontSize: {
    options: [
      { model: "8pt", title: "8pt" },
      { model: "9pt", title: "9pt" },
      { model: "10pt", title: "10pt" },
      { model: "11pt", title: "11pt" },
      { model: "12pt", title: "12pt" },
      { model: "13pt", title: "13pt" },
      { model: "14pt", title: "14pt" },
      { model: "15pt", title: "15pt" },
      { model: "16pt", title: "16pt" },
      { model: "36pt", title: "36pt" },
    ],
    supportAllValues: true,
  },
  htmlSupport: {
    allow: [{ name: /.*/, attributes: true, classes: true, styles: true }],
  },
  htmlEmbed: { showPreviews: true },
  link: {
    decorators: {
      addTargetToExternalLinks: true,
      defaultProtocol: "https://",
      toggleDownloadable: {
        mode: "manual",
        label: "Downloadable",
        attributes: { download: "file" },
      },
    },
  },
  removePlugins: [
    "ExportPdf",
    "ExportWord",
    "AIAssistant",
    "CKBox",
    "CKFinder",
    "EasyImage",
    "MultiLevelList",
    "RealTimeCollaborativeComments",
    "RealTimeCollaborativeTrackChanges",
    "RealTimeCollaborativeRevisionHistory",
    "PresenceList",
    "Comments",
    "TrackChanges",
    "TrackChangesData",
    "RevisionHistory",
    "Pagination",
    "WProofreader",
    "textPartLanguage",
    "htmlEmbed",
    "mediaEmbed",
    "MathType",
    "SlashCommand",
    "Template",
    "DocumentOutline",
    "TableOfContents",
    "PasteFromOfficeEnhanced",
    "CaseChange",
    "FormatPainter",
  ],
};

function ensureCkEditorLoaded(): Promise<void> {
  if (window.CKEDITOR?.ClassicEditor || window.ClassicEditor) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const existingScript = document.getElementById(CKEDITOR_SCRIPT_ID) as HTMLScriptElement | null;

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(), { once: true });
      existingScript.addEventListener("error", () => reject(new Error("Unable to load CKEditor")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = CKEDITOR_SCRIPT_ID;
    script.src = CKEDITOR_CDN_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Unable to load CKEditor"));
    document.head.appendChild(script);
  });
}

export const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(
  function RichTextEditor({ content, onChange, className }, ref) {
    const containerRef = useRef<HTMLDivElement>(null);
    const editorRef = useRef<CKEditorInstance | null>(null);
    const isInternalChange = useRef(false);
    const latestOnChange = useRef(onChange);
    latestOnChange.current = onChange;
    const initialContentRef = useRef(content);

    useImperativeHandle(ref, () => ({
      insertText(text: string) {
        const editor = editorRef.current;
        if (editor) {
          const selection = editor.model.document.selection;
          const currentAttributes = selection.getAttributes();
          const insertPosition = selection.focus;
          editor.model.change((writer) => {
            writer.insertText(text, currentAttributes, insertPosition);
          });
        }
      },
    }));

    useEffect(() => {
      if (!containerRef.current) return;

      let destroyed = false;

      void (async () => {
        await ensureCkEditorLoaded();

        const editorCreator = window.CKEDITOR?.ClassicEditor ?? window.ClassicEditor;
        if (!editorCreator || !containerRef.current) {
          return;
        }

        const editor = await editorCreator.create(containerRef.current, {
          ...EDITOR_CONFIG,
          licenseKey: CKEDITOR_LICENSE_KEY,
        });

        if (destroyed) {
          await editor.destroy();
          return;
        }

        editorRef.current = editor;

        editor.execute("fontFamily", { value: "Aptos, sans-serif" });
        editor.execute("fontSize", { value: "11pt" });

        if (initialContentRef.current) {
          editor.setData(initialContentRef.current);
        }

        editor.model.document.on("change:data", () => {
          isInternalChange.current = true;
          latestOnChange.current(editor.getData());
        });
      })();

      return () => {
        destroyed = true;
        if (editorRef.current) {
          void editorRef.current.destroy();
          editorRef.current = null;
        }
      };
    }, []);

    useEffect(() => {
      if (!editorRef.current) return;
      if (isInternalChange.current) {
        isInternalChange.current = false;
        return;
      }
      editorRef.current.setData(content);
    }, [content]);

    return <div ref={containerRef} className={className} />;
  },
);